import React, { useState, useEffect } from 'react';
import { Button, Modal, message, Tooltip, Select, InputNumber } from 'antd';
import { Form, InputGroup, Badge } from 'react-bootstrap';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ExperimentOutlined,
    SearchOutlined
} from '@ant-design/icons';
import { Input } from 'antd';
import { oneKlicks } from '../../SDK/oneclick';
import { getuserdata } from '../../services/apiService';
import Paginationnew from '../../Uitiles/Paginationnew';

const { TextArea } = Input;

const DiagnosticTests = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [userId, setUserId] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    // Modal states
    const [addModalVisible, setAddModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingTest, setEditingTest] = useState(null);

    // Form data
    const [formData, setFormData] = useState({
        test_name: '',
        category: '',
        price: 0,
        description: ''
    });

    const categoryOptions = [
        { value: 'Blood Tests', label: 'Blood Tests' },
        { value: 'Thyroid Tests', label: 'Thyroid Tests' },
        { value: 'Scan/Imaging', label: 'Scan/Imaging' },
        { value: 'Urine Tests', label: 'Urine Tests' },
        { value: 'Cardiac Tests', label: 'Cardiac Tests' },
        { value: 'Diabetes Tests', label: 'Diabetes Tests' },
        { value: 'Liver Tests', label: 'Liver Tests' },
        { value: 'Kidney Tests', label: 'Kidney Tests' },
        { value: 'Allergy Tests', label: 'Allergy Tests' },
        { value: 'Hormonal Tests', label: 'Hormonal Tests' },
        { value: 'Vitamin Tests', label: 'Vitamin Tests' },
        { value: 'COVID Tests', label: 'COVID Tests' },
        { value: 'Other', label: 'Other' },
    ];

    const categoryColors = {
        'Blood Tests': 'danger',
        'Thyroid Tests': 'primary',
        'Scan/Imaging': 'info',
        'Urine Tests': 'warning',
        'Cardiac Tests': 'danger',
        'Diabetes Tests': 'success',
        'Liver Tests': 'warning',
        'Kidney Tests': 'info',
        'Allergy Tests': 'primary',
        'Hormonal Tests': 'secondary',
        'Vitamin Tests': 'success',
        'COVID Tests': 'danger',
        'Other': 'secondary'
    };

    useEffect(() => {
        initializeData();
    }, [page, searchText, filterCategory]);

    const initializeData = async () => {
        const id = await getuserdata('id');
        setUserId(id);
        getTests(id);
    };

    const getTests = async (id) => {
        try {
            setLoading(true);
            const currentUserId = id || userId;
            const conditions = { diagnostic_id: currentUserId };

            if (filterCategory) {
                conditions.category = filterCategory;
            }

            const search = searchText ? {
                test_name: searchText,
            } : {};
            const pagination = { page, limit };

            const response = await oneKlicks.read('', 'diagnostic_tests', conditions, 'test_name ASC', search, pagination);

            if (response && response.data) {
                setData(response.data);
                setTotalPages(response.totalPages || 1);
            }
        } catch (error) {
            console.error('getTests error:', error);
            message.error('Failed to fetch tests');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setFormData({ test_name: '', category: '', price: 0, description: '' });
        setAddModalVisible(true);
    };

    const handleAddSave = async () => {
        if (!formData.test_name || !formData.category) {
            message.error('Please fill in test name and category');
            return;
        }

        try {
            setLoading(true);
            const payload = {
                diagnostic_id: userId,
                test_name: formData.test_name,
                category: formData.category,
                price: formData.price || 0,
                description: formData.description || '',
                created_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
            };

            const response = await oneKlicks.create('', 'diagnostic_tests', payload);
            if (response.success) {
                message.success('Test added successfully');
                setAddModalVisible(false);
                getTests(userId);
            } else {
                message.error(response.message || 'Failed to add');
            }
        } catch (error) {
            console.error('Add error:', error);
            message.error('Failed to add test');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (record) => {
        setEditingTest(record);
        setFormData({
            test_name: record.test_name || '',
            category: record.category || '',
            price: record.price || 0,
            description: record.description || ''
        });
        setEditModalVisible(true);
    };

    const handleEditSave = async () => {
        if (!formData.test_name || !formData.category) {
            message.error('Please fill in test name and category');
            return;
        }

        try {
            setLoading(true);
            const payload = {
                test_name: formData.test_name,
                category: formData.category,
                price: formData.price || 0,
                description: formData.description || '',
                updated_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
            };

            const response = await oneKlicks.update('', 'diagnostic_tests', payload, { id: editingTest.id });
            if (response.success) {
                message.success('Test updated successfully');
                setEditModalVisible(false);
                getTests(userId);
            } else {
                message.error(response.message || 'Failed to update');
            }
        } catch (error) {
            console.error('Update error:', error);
            message.error('Failed to update test');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (record) => {
        if (window.confirm(`Are you sure you want to delete "${record.test_name}"?`)) {
            try {
                const response = await oneKlicks.delete('', 'diagnostic_tests', { id: record.id });
                if (response && response.success) {
                    message.success('Test deleted successfully');
                    getTests(userId);
                }
            } catch (error) {
                message.error('Failed to delete test');
            }
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    return (
        <div className="container-fluid py-3">
            <div className="card mb-5">
                <div className="card-body">
                    <div className="align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <h5 className="mb-0 font-weight-bold mr-3">
                                <ExperimentOutlined className="me-2 text-primary" />
                                Tests & Prices
                            </h5>
                            <Badge bg="primary">Catalog</Badge>
                        </div>
                        <div className="d-flex justify-content-between align-items-center" style={{ gap: 12 }}>
                            <InputGroup className="flex-grow-1 me-2 h-40" style={{ maxWidth: '300px' }}>
                                <Form.Control
                                    placeholder="Search tests..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </InputGroup>
                            <Select
                                style={{ width: 180 }}
                                placeholder="Filter category"
                                options={[{ value: '', label: 'All Categories' }, ...categoryOptions]}
                                value={filterCategory}
                                onChange={setFilterCategory}
                                allowClear
                            />
                            <Button className="btn-primary h-40" onClick={handleAdd}>
                                <PlusOutlined /> Add Test
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0 align-middle">
                            <thead>
                                <tr>
                                    <th>Test Name</th>
                                    <th>Category</th>
                                    <th>Price</th>
                                    <th>Description</th>
                                    <th style={{ width: '150px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="5" className="text-center py-4">Loading...</td>
                                    </tr>
                                ) : data.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="text-center py-4">No tests found</td>
                                    </tr>
                                ) : (
                                    data.map((item) => (
                                        <tr key={item.id}>
                                            <td data-label="Test Name">
                                                <strong>{item.test_name}</strong>
                                            </td>
                                            <td data-label="Category">
                                                <Badge bg={categoryColors[item.category] || 'secondary'}>
                                                    {item.category}
                                                </Badge>
                                            </td>
                                            <td data-label="Price">
                                                <span className="fw-bold text-success">₹{item.price || 0}</span>
                                            </td>
                                            <td data-label="Description">
                                                <div style={{ maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {item.description || '-'}
                                                </div>
                                            </td>
                                            <td data-label="Actions">
                                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                                    <Button
                                                        className="btn-info"
                                                        onClick={() => handleEdit(item)}
                                                        style={{ fontSize: '12px', padding: '4px 8px' }}
                                                    >
                                                        <EditOutlined />
                                                    </Button>
                                                    <Button
                                                        className="btn-danger"
                                                        onClick={() => handleDelete(item)}
                                                        style={{ fontSize: '12px', padding: '4px 8px' }}
                                                    >
                                                        <DeleteOutlined />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <Paginationnew currentPage={page} totalPages={totalPages} handlePageChange={handlePageChange} />
                </div>
            </div>

            {/* Add Modal */}
            <Modal
                title="Add New Test"
                open={addModalVisible}
                onCancel={() => setAddModalVisible(false)}
                onOk={handleAddSave}
                confirmLoading={loading}
                width={600}
                centered
            >
                <div className="mb-3">
                    <label className="form-label fw-bold">Test Name *</label>
                    <Input
                        value={formData.test_name}
                        onChange={(e) => setFormData({ ...formData, test_name: e.target.value })}
                        placeholder="e.g., Complete Blood Count (CBC)"
                    />
                </div>
                <div className="mb-3">
                    <label className="form-label fw-bold">Category *</label>
                    <Select
                        className="w-100"
                        placeholder="Select category"
                        options={categoryOptions}
                        value={formData.category || undefined}
                        onChange={(val) => setFormData({ ...formData, category: val })}
                    />
                </div>
                <div className="mb-3">
                    <label className="form-label fw-bold">Price (₹)</label>
                    <InputNumber
                        className="w-100"
                        min={0}
                        value={formData.price}
                        onChange={(val) => setFormData({ ...formData, price: val })}
                        formatter={value => `₹ ${value}`}
                        parser={value => value.replace('₹ ', '')}
                    />
                </div>
                <div className="mb-3">
                    <label className="form-label fw-bold">Description</label>
                    <TextArea
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Enter test description..."
                    />
                </div>
            </Modal>

            {/* Edit Modal */}
            <Modal
                title="Edit Test"
                open={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                onOk={handleEditSave}
                confirmLoading={loading}
                width={600}
                centered
            >
                <div className="mb-3">
                    <label className="form-label fw-bold">Test Name *</label>
                    <Input
                        value={formData.test_name}
                        onChange={(e) => setFormData({ ...formData, test_name: e.target.value })}
                    />
                </div>
                <div className="mb-3">
                    <label className="form-label fw-bold">Category *</label>
                    <Select
                        className="w-100"
                        placeholder="Select category"
                        options={categoryOptions}
                        value={formData.category || undefined}
                        onChange={(val) => setFormData({ ...formData, category: val })}
                    />
                </div>
                <div className="mb-3">
                    <label className="form-label fw-bold">Price (₹)</label>
                    <InputNumber
                        className="w-100"
                        min={0}
                        value={formData.price}
                        onChange={(val) => setFormData({ ...formData, price: val })}
                        formatter={value => `₹ ${value}`}
                        parser={value => value.replace('₹ ', '')}
                    />
                </div>
                <div className="mb-3">
                    <label className="form-label fw-bold">Description</label>
                    <TextArea
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                </div>
            </Modal>
        </div>
    );
};

export default DiagnosticTests;
